import { ChevronUp, ChevronsUp } from 'lucide-react';
import type { OrgUnitElevation } from '@pams/shared';
import { elevationSentence } from './elevationWords';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §11] THE SMALL MARK BESIDE A UNIT'S NAME WHEN ITS NODAL OFFICERS ACT HIGHER UP.
 *
 *  Rendered on the Organisation page's tree and list and on the Officers list's Unit column —
 *  beside a unit NAME, because the setting belongs to the unit and applies to every nodal officer
 *  posted there. Nothing renders at `NONE`, so an ordinary row is byte-identical.
 *
 *  ⚠️ THE SHAPE CARRIES THE LEVEL — one chevron for one level up, two for two — and the colour is
 *  the `elevation` token's indigo, which is not a status hue. Shape first, so it survives
 *  greyscale and a colour-blind reader (`CoverageBar`'s rule).
 *
 *  ⚠️ A NATIVE `title`, NEVER AN ANTD `Tooltip`: one per row is divergence #82's measured cost
 *  (64 s → 119 s on a list), and it is invisible to `census.spec.ts` besides. The accessible name
 *  is the same sentence, on `role="img"`, so a screen reader hears it where the eye sees a chevron.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ElevationMark = ({
  elevation,
  actingAt,
}: {
  elevation: OrgUnitElevation | undefined;
  /** The name of the unit it acts at, when the caller has it; the sentence omits it otherwise. */
  actingAt?: string | undefined;
}) => {
  if (elevation === undefined || elevation === 'NONE') return null;
  const Icon = elevation === 'TWO_UP' ? ChevronsUp : ChevronUp;
  const sentence = elevationSentence(elevation, actingAt);
  return (
    <span
      role="img"
      aria-label={sentence}
      title={sentence}
      data-testid="elevation-mark"
      data-elevation={elevation}
      className="inline-flex shrink-0 items-center text-elevation"
    >
      <Icon className="size-3.5" strokeWidth={2.5} aria-hidden />
    </span>
  );
};
